import { motion, AnimatePresence } from 'framer-motion'
import { useState, useRef } from 'react'
import { ChevronDown, Scale, UserCheck, Shield, Heart } from 'lucide-react'

const fadeUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
}

export default function Direitos() {
    /* Todos os direitos abertos por defeito: o texto legal é a substância da página —
       fica no DOM (indexável, pesquisável com Ctrl+F, imprimível) e permite leitura
       contínua. As pills passam a âncoras que abrem e levam ao direito escolhido. */
    const [openAccordions, setOpenAccordions] = useState(() => new Set([0, 1, 2, 3]))
    const accordionRefs = useRef([])

    const toggleAccordion = (i) => setOpenAccordions((prev) => {
        const next = new Set(prev)
        if (next.has(i)) next.delete(i); else next.add(i)
        return next
    })

    const handlePillClick = (i) => {
        setOpenAccordions((prev) => new Set(prev).add(i))
        setTimeout(() => {
            const el = accordionRefs.current[i]
            if (el) {
                const top = el.getBoundingClientRect().top + window.scrollY - 120
                const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
                    document.documentElement.classList.contains('a11y-pause-animations')
                window.scrollTo({ top, behavior: reduzido ? 'auto' : 'smooth' })
            }
        }, 80)
    }

    const rights = [
        {
            icon: Scale,
            color: 'from-rose-500 to-pink-600',
            bg: 'bg-rose-50',
            accent: 'text-rose-700',
            title: 'Direito à igualdade',
            content: `A Constituição da República Portuguesa (CRP) determina que todas as pessoas têm direito à identidade pessoal, ao desenvolvimento da personalidade, à capacidade civil, à cidadania, ao bom nome e reputação, à imagem, à palavra, à intimidade da vida privada e familiar e à proteção legal contra quaisquer formas de discriminação.

Prevê também a legislação portuguesa que a discriminação positiva possa e deva existir de forma a criar situações de igualdade material e equidade.

A legislação europeia determina que a discriminação de pessoas LGBTI+ no local de trabalho é proibida e obriga a que seja assegurada proteção jurídica contra a discriminação a todas as pessoas no acesso ao emprego e à formação, bem como nas questões relacionadas com as condições de trabalho, a remuneração e o despedimento.

A proibição da discriminação é aplicável a pessoas que fornecem bens ou prestam serviços disponíveis ao público e sejam oferecidos fora do domínio da vida privada e familiar e das transações efetuadas neste contexto.`
        },
        {
            icon: UserCheck,
            color: 'from-rose-500 to-pink-600',
            bg: 'bg-rose-50',
            accent: 'text-rose-700',
            title: 'Direito à autodeterminação da identidade de género e expressão de género e à proteção das características sexuais de cada pessoa',
            content: `A legislação portuguesa estabelece o direito à autodeterminação da identidade de género e expressão de género cujo reconhecimento jurídico passa pela abertura de um procedimento de mudança da menção do sexo no registo civil e da consequente alteração de nome próprio. Este procedimento pode ser instaurado por pessoas de nacionalidade portuguesa, maiores de idade e que não se mostrem interditas ou inabilitadas por anomalia psíquica, cuja identidade de género não corresponda ao sexo atribuído à nascença.`
        },
        {
            icon: Shield,
            color: 'from-rose-500 to-pink-600',
            bg: 'bg-rose-50',
            accent: 'text-rose-700',
            title: 'Direito à segurança',
            content: `Todas as pessoas têm o direito a sentirem-se seguras em qualquer contexto, público ou privado, e à garantia de medidas que protejam e promovam a sua integridade física e saúde mental.

Todas as vítimas de crime merecem ser reconhecidas como tal e ser tratadas de forma respeitosa e adaptada às suas necessidades e características individuais.`
        },
        {
            icon: Heart,
            color: 'from-rose-500 to-pink-600',
            bg: 'bg-rose-50',
            accent: 'text-rose-700',
            title: 'Direito à saúde',
            content: `Enquanto direito fundamental, o acesso à saúde não pode em nenhum momento ser limitado por barreiras adicionais em razão da orientação sexual, identidade de género e características sexuais. No entanto, existem ainda lacunas que se manifestam em práticas discriminatórias e não adaptadas nos cuidados de saúde, nomeadamente a pessoas trans e intersexo, que devem ser eliminadas para garantir o direito à saúde efetivo para todas as pessoas.`
        }
    ]

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="min-h-screen bg-white"
        >
            <title>Direitos das Pessoas LGBTI+ na Lei Portuguesa | APAV</title>
            <meta name="description" content="Direito à igualdade, à autodeterminação da identidade de género, à segurança e à saúde: o que a lei portuguesa e europeia garante às pessoas LGBTI+." />
            <link rel="canonical" href="https://lgbti.apav.pt/Direitos" />
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-indigo-900 to-violet-900 py-20 lg:py-28">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-1/3 w-96 h-96 bg-violet-400 rounded-full filter blur-3xl -translate-y-1/2" />
                    <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-brand-400 rounded-full filter blur-3xl translate-y-1/2" />
                </div>
                <div className="container-custom relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                    >
                        <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                            Direitos
                        </h1>
                    </motion.div>
                </div>
            </section>

            {/* Rights Overview Pills */}
            <section className="py-8 bg-white border-b border-slate-100">
                <div className="container-custom">
                    <div className="flex flex-wrap gap-3">
                        {rights.map((right, i) => {
                            const Icon = right.icon
                            return (
                                <motion.button
                                    key={i}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.08 }}
                                    onClick={() => handlePillClick(i)}
                                    aria-pressed={openAccordions.has(i)}
                                    className={`flex items-center gap-2 px-4 py-2.5 rounded-full border transition-all duration-300 font-semibold text-sm focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2
                                        ${openAccordions.has(i)
                                            ? `bg-gradient-to-r ${right.color} text-white border-transparent shadow-lg`
                                            : `${right.bg} ${right.accent} border-current/20 hover:shadow-md`
                                        }`}
                                >
                                    <Icon size={15} />
                                    {right.title.split('à ')[1]?.split(' e ')[0] || right.title.split(' ')[2]}
                                </motion.button>
                            )
                        })}
                    </div>
                </div>
            </section>

            {/* Accordion */}
            <section className="py-10 lg:py-12 bg-slate-50">
                <div className="container-custom">
                    <div className="space-y-4">
                        {rights.map((right, index) => {
                            const Icon = right.icon
                            const isOpen = openAccordions.has(index)
                            return (
                                <motion.div
                                    key={index}
                                    ref={(el) => { accordionRefs.current[index] = el }}
                                    variants={fadeUp}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5, delay: index * 0.08 }}
                                    className={`rounded-2xl overflow-hidden border transition-all duration-300 ${isOpen ? 'border-brand-200 shadow-lg shadow-brand-50' : 'border-slate-200 bg-white shadow-xs'}`}
                                >
                                    <h2>
                                    <button
                                        onClick={() => toggleAccordion(index)}
                                        aria-expanded={isOpen}
                                        aria-controls={`direito-${index}`}
                                        className={`w-full px-6 py-5 flex items-center gap-4 justify-between transition-colors text-left focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-inset ${isOpen ? 'bg-white' : 'bg-white hover:bg-slate-50'}`}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className={`p-2.5 rounded-xl bg-gradient-to-br ${right.color} shadow-md shrink-0`}>
                                                <Icon size={20} className="text-white" />
                                            </div>
                                            <span className="text-base lg:text-lg font-bold text-slate-900 pr-2">{right.title}</span>
                                        </div>
                                        <div className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-brand-600 text-white rotate-180' : 'bg-slate-100 text-slate-600'}`}>
                                            <ChevronDown size={18} />
                                        </div>
                                    </button>
                                    </h2>
                                    <AnimatePresence>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: 'auto', opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.3, ease: 'easeInOut' }}
                                                className="overflow-hidden"
                                                id={`direito-${index}`}
                                            >
                                                <div className={`px-6 pb-6 pt-2 ${right.bg} border-t border-slate-100`}>
                                                    <p className="text-slate-700 leading-relaxed whitespace-pre-line text-base lg:text-lg md:pl-14 max-w-3xl">
                                                        {right.content}
                                                    </p>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </motion.div>
                            )
                        })}
                    </div>
                </div>
            </section>
        </motion.div>
    )
}
